import type { LangCode } from "@/lib/i18n";

type AuthDict = {
  signIn: string;
  signOut: string;
  signUp: string;
  back: string;
  welcomeBack: string;
  createAccount: string;
  signInSubtitle: string;
  signUpSubtitle: string;
  continueGoogle: string;
  or: string;
  email: string;
  password: string;
  createAccountBtn: string;
  pleaseWait: string;
  checkEmail: string;
  emailConfirmed: string;
  emailNotConfirmed: string;
  resendConfirmation: string;
  somethingWrong: string;
  googleFailed: string;
  acceptPrefix: string;
  termsLabel: string;
  andText: string;
  privacyLabel: string;
  mustAccept: string;
};

export const AUTH_T: Record<LangCode, AuthDict> = {
  en: {
    signIn: "Sign In",
    signOut: "Sign out",
    signUp: "Sign Up",
    back: "Back to Routify",
    welcomeBack: "Welcome back",
    createAccount: "Create your account",
    signInSubtitle: "Sign in to save routes and access your history.",
    signUpSubtitle: "Start planning smarter routes today.",
    continueGoogle: "Continue with Google",
    or: "or",
    email: "Email",
    password: "Password",
    createAccountBtn: "Create Account",
    pleaseWait: "Please wait…",
    checkEmail: "Check your email for the confirmation link.",
    emailConfirmed: "Email confirmed! You can now sign in.",
    emailNotConfirmed: "Please confirm your email before signing in. Check your inbox for the confirmation link.",
    resendConfirmation: "Resend confirmation email",
    somethingWrong: "Something went wrong.",
    googleFailed: "Google sign-in failed.",
    acceptPrefix: "By creating an account you accept our",
    termsLabel: "Terms",
    andText: "and",
    privacyLabel: "Privacy Policy",
    mustAccept: "Please accept the Terms and Privacy Policy to continue.",
  },
  pt: {
    signIn: "Entrar",
    signOut: "Sair",
    signUp: "Criar conta",
    back: "Voltar ao Routify",
    welcomeBack: "Bem-vindo de volta",
    createAccount: "Cria a tua conta",
    signInSubtitle: "Entra para guardar rotas e aceder ao teu histórico.",
    signUpSubtitle: "Começa a planear rotas mais inteligentes hoje.",
    continueGoogle: "Continuar com Google",
    or: "ou",
    email: "Email",
    password: "Palavra-passe",
    createAccountBtn: "Criar conta",
    pleaseWait: "Aguarda…",
    checkEmail: "Verifica o teu email para o link de confirmação.",
    emailConfirmed: "Email confirmado! Já podes entrar.",
    emailNotConfirmed: "Por favor confirma o teu email antes de entrar. Verifica a tua caixa de entrada.",
    resendConfirmation: "Reenviar email de confirmação",
    somethingWrong: "Algo correu mal.",
    googleFailed: "Falha ao entrar com Google.",
    acceptPrefix: "Ao criar conta aceitas os nossos",
    termsLabel: "Termos",
    andText: "e",
    privacyLabel: "Política de Privacidade",
    mustAccept: "Tens de aceitar os Termos e a Política de Privacidade para continuar.",
  },
  fr: {
    signIn: "Connexion",
    signOut: "Déconnexion",
    signUp: "S'inscrire",
    back: "Retour à Routify",
    welcomeBack: "Bon retour",
    createAccount: "Créer votre compte",
    signInSubtitle: "Connectez-vous pour sauvegarder vos itinéraires et votre historique.",
    signUpSubtitle: "Commencez à planifier des itinéraires plus intelligents.",
    continueGoogle: "Continuer avec Google",
    or: "ou",
    email: "E-mail",
    password: "Mot de passe",
    createAccountBtn: "Créer un compte",
    pleaseWait: "Veuillez patienter…",
    checkEmail: "Vérifiez votre e-mail pour le lien de confirmation.",
    emailConfirmed: "E-mail confirmé ! Vous pouvez maintenant vous connecter.",
    emailNotConfirmed: "Veuillez confirmer votre e-mail avant de vous connecter. Vérifiez votre boîte de réception.",
    resendConfirmation: "Renvoyer l'e-mail de confirmation",
    somethingWrong: "Une erreur s'est produite.",
    googleFailed: "Échec de la connexion Google.",
    acceptPrefix: "En créant un compte vous acceptez nos",
    termsLabel: "Conditions",
    andText: "et",
    privacyLabel: "Politique de Confidentialité",
    mustAccept: "Veuillez accepter les Conditions et la Politique de Confidentialité pour continuer.",
  },
  es: {
    signIn: "Iniciar sesión",
    signOut: "Cerrar sesión",
    signUp: "Registrarse",
    back: "Volver a Routify",
    welcomeBack: "Bienvenido de nuevo",
    createAccount: "Crea tu cuenta",
    signInSubtitle: "Inicia sesión para guardar rutas y acceder a tu historial.",
    signUpSubtitle: "Empieza a planear rutas más inteligentes hoy.",
    continueGoogle: "Continuar con Google",
    or: "o",
    email: "Correo",
    password: "Contraseña",
    createAccountBtn: "Crear cuenta",
    pleaseWait: "Espera…",
    checkEmail: "Revisa tu correo para el enlace de confirmación.",
    emailConfirmed: "¡Correo confirmado! Ya puedes iniciar sesión.",
    emailNotConfirmed: "Por favor confirma tu correo antes de iniciar sesión. Revisa tu bandeja de entrada.",
    resendConfirmation: "Reenviar correo de confirmación",
    somethingWrong: "Algo salió mal.",
    googleFailed: "Error al iniciar sesión con Google.",
    acceptPrefix: "Al crear una cuenta aceptas nuestros",
    termsLabel: "Términos",
    andText: "y",
    privacyLabel: "Política de Privacidad",
    mustAccept: "Debes aceptar los Términos y la Política de Privacidad para continuar.",
  },
  de: {
    signIn: "Anmelden",
    signOut: "Abmelden",
    signUp: "Registrieren",
    back: "Zurück zu Routify",
    welcomeBack: "Willkommen zurück",
    createAccount: "Konto erstellen",
    signInSubtitle: "Melde dich an, um Routen zu speichern und auf deinen Verlauf zuzugreifen.",
    signUpSubtitle: "Beginne, smartere Routen zu planen.",
    continueGoogle: "Mit Google fortfahren",
    or: "oder",
    email: "E-Mail",
    password: "Passwort",
    createAccountBtn: "Konto erstellen",
    pleaseWait: "Bitte warten…",
    checkEmail: "Prüfe deine E-Mails für den Bestätigungslink.",
    emailConfirmed: "E-Mail bestätigt! Du kannst dich jetzt anmelden.",
    emailNotConfirmed: "Bitte bestätige deine E-Mail vor der Anmeldung. Prüfe deinen Posteingang.",
    resendConfirmation: "Bestätigungs-E-Mail erneut senden",
    somethingWrong: "Etwas ist schiefgelaufen.",
    googleFailed: "Google-Anmeldung fehlgeschlagen.",
    acceptPrefix: "Mit der Kontoerstellung akzeptierst du unsere",
    termsLabel: "AGB",
    andText: "und",
    privacyLabel: "Datenschutzerklärung",
    mustAccept: "Bitte akzeptiere die AGB und Datenschutzerklärung, um fortzufahren.",
  },
  ja: {
    signIn: "ログイン",
    signOut: "ログアウト",
    signUp: "登録",
    back: "Routifyに戻る",
    welcomeBack: "おかえりなさい",
    createAccount: "アカウントを作成",
    signInSubtitle: "ログインしてルートを保存し、履歴にアクセスしましょう。",
    signUpSubtitle: "もっと賢いルートのプランニングを始めましょう。",
    continueGoogle: "Googleで続行",
    or: "または",
    email: "メール",
    password: "パスワード",
    createAccountBtn: "アカウント作成",
    pleaseWait: "お待ちください…",
    checkEmail: "確認リンクをメールで確認してください。",
    emailConfirmed: "メールが確認されました！ログインできます。",
    emailNotConfirmed: "ログインする前にメールを確認してください。受信トレイを確認してください。",
    resendConfirmation: "確認メールを再送信",
    somethingWrong: "問題が発生しました。",
    googleFailed: "Googleログインに失敗しました。",
    acceptPrefix: "アカウント作成により、以下に同意したものとみなされます:",
    termsLabel: "利用規約",
    andText: "および",
    privacyLabel: "プライバシーポリシー",
    mustAccept: "続行するには利用規約とプライバシーポリシーに同意してください。",
  },
  it: {
    signIn: "Accedi",
    signOut: "Esci",
    signUp: "Registrati",
    back: "Torna a Routify",
    welcomeBack: "Bentornato",
    createAccount: "Crea il tuo account",
    signInSubtitle: "Accedi per salvare i percorsi e accedere alla cronologia.",
    signUpSubtitle: "Inizia oggi a pianificare percorsi più intelligenti.",
    continueGoogle: "Continua con Google",
    or: "oppure",
    email: "Email",
    password: "Password",
    createAccountBtn: "Crea account",
    pleaseWait: "Attendere…",
    checkEmail: "Controlla la tua email per il link di conferma.",
    emailConfirmed: "Email confermata! Ora puoi accedere.",
    emailNotConfirmed: "Conferma la tua email prima di accedere. Controlla la posta in arrivo per il link di conferma.",
    resendConfirmation: "Invia di nuovo l'email di conferma",
    somethingWrong: "Qualcosa è andato storto.",
    googleFailed: "Accesso con Google non riuscito.",
    acceptPrefix: "Creando un account accetti i nostri",
    termsLabel: "Termini",
    andText: "e",
    privacyLabel: "Informativa sulla privacy",
    mustAccept: "Accetta i Termini e l'Informativa sulla privacy per continuare.",
  },
};
